import { get, isEmpty, isPlainObject, last, omit } from "lodash"
import { lineString, point } from "@turf/helpers"
import { UnitDistanceOptions } from "venue-js"

import { IGraphFeatureOptions, IStepElement } from "interfaces"

import { DESTINATION_MARKER_ID, ORIGIN_MARKER_ID } from "components/IndoorMap"
import { VERTICAL_TRAVERSAL_CATEGORY, WALKABLE_CATEGORY } from "../../constants"

export const createArrivalVenueSDKStep = (steps) => {
  const lastStep = (last(steps) ?? {}) as object
  //@ts-ignore
  const destination = lastStep.to || {}
  const path = [destination.point]

  return {
    ...lastStep,
    destination: destination,
    origin: destination,
    instructions: [],
    intermediaryCategory: null,
    intermediaryIds: [],
    path,
    from: destination,
    to: destination,
    description: { text: "Arrived at destination" },
  }
}

const isVerticalStep = (step) =>
  Array.isArray(step?.ordinals) && step.ordinals.length > 1

// Finds the path vertex matching an instruction's `at.center`. Matches on
// lng/lat only (not altitude) since `at.center` and `path` share the same
// unmodified coordinates coming out of the SDK.
const findVertexIndex = (path, center) => {
  if (!Array.isArray(path) || !Array.isArray(center)) return -1
  return path.findIndex(
    (coord) => coord[0] === center[0] && coord[1] === center[1]
  )
}

// venue-js's `instruction.bearing` is turf's signed (-180, 180] rhumb bearing;
// the map's own bearing helpers (getLineStringBearing, getSuitablyValueBetweenBearings)
// work in [0, 360). Normalize here so both sources are interchangeable downstream.
const normalizeBearing = (degrees) =>
  typeof degrees === "number" ? ((degrees % 360) + 360) % 360 : undefined

/**
 * Splits a Step into one step per turn instruction, so each turn becomes its
 * own navigable step: [step description] -> [turn 1] -> [turn 2] -> ...
 *
 * The synthetic "start" instruction venue-js attaches at `path[0]` (and any
 * `turn: "straight"` instruction, which only ever occurs there) is dropped as
 * a text row — the step's own description already covers that ground as
 * segment 1 — but its `bearing.to` is kept to orient segment 1's camera (see
 * below), since it's still the precise heading leaving the origin/opening.
 *
 * Vertical steps never carry instructions and pass through unchanged. Steps
 * whose instructions lack `template` (older SDK shape) also pass through
 * unchanged rather than producing untranslatable rows.
 */
export const flattenStepByInstructions = (step) => {
  const { instructions, path } = step

  if (
    isVerticalStep(step) ||
    !Array.isArray(instructions) ||
    !instructions.length ||
    !Array.isArray(path) ||
    path.length < 2
  ) {
    return [step]
  }

  // The instruction at path[0] — dropped as a text row (segment 0 keeps the step's own
  // description) but its bearing.to is still the precise heading leaving the origin/opening.
  const startInstruction = instructions.find(
    (instruction) => findVertexIndex(path, instruction?.at?.center) === 0
  )

  const turns = instructions.filter((instruction) => {
    if (instruction?.turn === "straight") return false
    return findVertexIndex(path, instruction?.at?.center) > 0
  })

  if (!turns.length || turns.some((turn) => !turn?.template)) {
    return [step]
  }

  const cutIndexes = [
    ...new Set(turns.map((turn) => findVertexIndex(path, turn.at.center))),
  ].sort((a, b) => a - b)

  const boundaries = [0, ...cutIndexes, path.length - 1]
  const stepWithoutInstructions = omit(step, ["instructions"])

  return boundaries.slice(0, -1).map((startIndex, segmentIndex) => {
    const endIndex = boundaries[segmentIndex + 1]
    const segmentPath = path.slice(startIndex, endIndex + 1)
    // segment 0 keeps the step's own description; later segments are
    // described by the turn that begins them.
    const turn = segmentIndex === 0 ? null : turns[segmentIndex - 1]

    const sourceInstruction = segmentIndex === 0 ? startInstruction : turn
    const bearing = normalizeBearing(sourceInstruction?.bearing?.to)

    return {
      ...stepWithoutInstructions,
      turn: turn?.turn,
      // Segment 0 is oriented by the dropped start instruction's heading; later segments by
      // the turn that begins them — both are the precise vertex-to-next-vertex bearing, not
      // a chord across the whole segment.
      bearing,
      description: turn
        ? {
            template: turn.template,
            options: turn.options,
          }
        : step.description,
      // Segment 0 starts at the step's real origin, so its source (including a live
      // "coordinate"/geolocation source, if any) carries through unchanged. Later segments
      // start at a computed turn vertex, not the original origin — a "coordinate" source
      // there would be wrong, and would wrongly force useStepByStepMap to use the default
      // camera bearing instead of this segment's own instruction bearing.
      from: {
        ...step.from,
        point: segmentPath[0],
        ...(segmentIndex > 0 && step.from.source?.type === "coordinate"
          ? { source: undefined }
          : {}),
      },
      to: { ...step.to, point: segmentPath[segmentPath.length - 1] },
      path: segmentPath,
    }
  })
}

export const transformStepsToGeometriesVenueSDK = (steps = []) => {
  if (!steps?.length) return []

  const firstStep = steps[0]
  const lastStep = steps[steps.length - 1]
  const routeOrigin: IStepElement = {
    id: ORIGIN_MARKER_ID,
    feature_type: "origin-marker",
    geometry: point(firstStep.from.point)?.geometry,
    properties: {
      ordinal: firstStep.from.ordinal ?? null,
      intermediaryIds: firstStep.intermediaryIds ?? [],
    },
  }
  const routeDestination: IStepElement = {
    id: DESTINATION_MARKER_ID,
    feature_type: "destination-marker",
    geometry: point(lastStep.to.point)?.geometry,
    properties: {
      ordinal: lastStep.to.ordinal ?? null,
      intermediaryIds: lastStep.intermediaryIds ?? [],
    },
  }

  const stepGeometries: IStepElement[] = steps.map((step, index) => {
    const { from, intermediaryCategory, intermediaryIds } = step

    // If geolocation is available at the origin in this step, use the default bearing to set the map view for the route.
    const hasGeolocationOrigin = from?.source?.type === "coordinate"

    // check the step is the vertical step or not
    const isVerticalStep =
      VERTICAL_TRAVERSAL_CATEGORY.includes(intermediaryCategory) &&
      step.ordinals.length > 1

    const stepFeature =
      step.path?.length >= 2 ? lineString(step.path) : point(step.path[0])

    const stepPath = isVerticalStep ? point(from.point) : stepFeature

    return {
      ...stepPath,
      isVerticalStep: isVerticalStep,
      isLastStep: steps.length - 1 === index,
      hasGeolocationOrigin,
      bearing: step.bearing,
      properties: {
        ordinal: from.ordinal ?? null,
        intermediaryIds: intermediaryIds ?? [],
      },
      id: `step-${index + 1}`,
    }
  })
  stepGeometries.unshift(routeOrigin)
  stepGeometries.push(routeDestination)
  return stepGeometries
}

// IMDF unit category slugs that fall back to a bare category name when the unit has no
// occupant/alt_name. Sourced from the same vertical/walkable category lists the rest of this
// module already uses, so a new category added there is picked up automatically instead of
// needing a second hand-maintained list here.
const UNIT_CATEGORIES = [
  ...VERTICAL_TRAVERSAL_CATEGORY,
  ...WALKABLE_CATEGORY.filter((category) => category !== "walkway"),
]

// venue-js's own English fallback literals for landmark names that have no real
// occupant/name (see services/landmark/utils/toLandmark.ts). No slug is exposed for these —
// matching is on the literal English word, so a real occupant/amenity that happens to share
// the exact-case English name would collide. Low-frequency in practice.
const LANDMARK_FALLBACK_KEYS = {
  "Entrance / Exit": "landmark.entrance_exit",
  amphitheater: "amenity.amphitheater",
  landmark: "amenity.landmark",
  meetingpoint: "amenity.meetingpoint",
  information: "amenity.information",
}

// Converts a category/landmark fallback value (string or {en} object) to an i18n key
// (e.g. "escalator" → "unit.escalator", "Entrance / Exit" → "landmark.entrance_exit").
// Real translated names (occupant names, alt_names) are returned unchanged.
export const normalizePlaceName = (value) => {
  const name = typeof value === "string" ? value : get(value, "en")
  if (typeof name !== "string") return value
  if (UNIT_CATEGORIES.includes(name)) return `unit.${name}`
  if (LANDMARK_FALLBACK_KEYS[name]) return LANDMARK_FALLBACK_KEYS[name]
  return value
}

// Maps a raw InstructionTurn value (e.g. "slight left") to its i18n key (e.g.
// "turn.slight_left"). Only left/right/slight left/slight right ever land inside a
// template's {{turn}} placeholder — straight instructions are dropped in
// flattenStepByInstructions, and the U-turn templates hardcode "Make a U-turn" without
// interpolating turn — but all 7 InstructionTurn values are mapped for completeness (e.g.
// for a future icon aria-label).
const TURN_KEYS = {
  left: "turn.left",
  right: "turn.right",
  "slight left": "turn.slight_left",
  "slight right": "turn.slight_right",
  straight: "turn.straight",
  "U-turn left": "turn.uturn_left",
  "U-turn right": "turn.uturn_right",
}

export const normalizeTurnName = (turn) => TURN_KEYS[turn] ?? turn

export const normalizeUnitDistanceOptions = (options: IGraphFeatureOptions) => {
  if (!isPlainObject(options) || isEmpty(options)) return
  return {
    byCategory: options,
  } as Partial<UnitDistanceOptions>
}
